/**
 * @fileoverview Deterministic preview seed for `isRandomized` option lists
 * @module screen-view-v2/context/PreviewOptionsRandomizationProvider
 *
 * The editor's canvas node and its screen preview must show the SAME screen the
 * visitor gets (owner ruling R-POST-3): the same collapse past
 * `OPTIONS_DISCLOSURE_THRESHOLD`, and — for a list the owner marked
 * `isRandomized` — the same eight of a shuffled order rather than the document's
 * first eight, which is an order no visitor ever sees.
 *
 * A visitor's order cannot be reproduced in the editor (it is seeded from their
 * `platformUserId`, `web-shell/src/engine/utils/randomizationSeed.ts`), so the
 * editor gets ONE deterministic sample instead: `preview:${screenId}`
 * (controller ruling R-W11-5). Constant per screen, so the canvas node and the
 * editor dialog agree and the order does not resample on every render; no user
 * identity in it, so it is not mistaken for a particular visitor's order.
 *
 * The shuffle itself is not re-implemented here — `OptionsList` runs the shared
 * `seededFisherYatesShuffle` on whatever seed the context carries, and applies
 * it ONLY when the document says `isRandomized === true`. This provider decides
 * the seed and nothing else.
 */

import React, { useMemo } from 'react';
import { OptionsRandomizationContext } from './OptionsRandomizationContext';

/** Prefix that marks a seed as the editor's sample order, not a visitor's. */
export const PREVIEW_OPTIONS_SEED_PREFIX = 'preview:';

/**
 * The deterministic preview seed for a screen, or `null` when the surface has
 * no screen identity to seed from (a template palette, an unsaved draft) — in
 * which case `OptionsList` falls back to document order, as it does under SSR.
 */
export function computePreviewOptionsRandomization(
  screenId: string | null | undefined,
): { seedKey: string } | null {
  if (!screenId) return null;
  return { seedKey: `${PREVIEW_OPTIONS_SEED_PREFIX}${screenId}` };
}

export interface PreviewOptionsRandomizationProviderProps {
  /** The screen being previewed (`StepNode.id`, = the canvas `entityId`). */
  screenId: string | null | undefined;
  children: React.ReactNode;
}

/**
 * Mounts the deterministic preview seed for everything rendered inside it.
 * Used at the editor's render boundaries (canvas node, screen editor preview);
 * the published runtime mounts `OptionsRandomizationContext` itself, with the
 * visitor's seed, and never renders this.
 */
export function PreviewOptionsRandomizationProvider({
  screenId,
  children,
}: PreviewOptionsRandomizationProviderProps): React.ReactElement {
  const value = useMemo(() => computePreviewOptionsRandomization(screenId), [screenId]);

  return (
    <OptionsRandomizationContext.Provider value={value}>
      {children}
    </OptionsRandomizationContext.Provider>
  );
}

PreviewOptionsRandomizationProvider.displayName = 'PreviewOptionsRandomizationProvider';
